import { babel } from '@/packages/vue-i18n'

const SHIPMENT_ERROR_CODE = 'ShipmentError'
const SHIPMENT_MESSAGE_PREFIX = 'shipment.'
const SHIPMENT_VALIDATION_ERROR = 'shipment.validation_error'
const SHIPMENT_ERROR_I18N_PREFIX = 'common.messages.shipment-errors.'

const formatParameters = (params) => {
	if (params === null || params === undefined || params === '') return ''
	if (Array.isArray(params)) return params.map(formatParameters).filter(Boolean).join(', ')
	if (typeof params === 'object') {
		return Object.entries(params)
			.map(([key, value]) => {
				const formatted = formatParameters(value)
				return formatted ? `${key}: ${formatted}` : ''
			})
			.filter(Boolean)
			.join(', ')
	}
	return String(params)
}

const EMBEDDED_DETAILS_PATTERN = /:\s*[[{].*$/s

const normalizeVariables = (variables) => {
	if (!variables || typeof variables !== 'object') return {}
	return Object.entries(variables).reduce((acc, [key, value]) => {
		acc[key] = Array.isArray(value) ? value.join(', ') : value
		return acc
	}, {})
}

// Backend errors can carry a translatable payload
// { code: 'shipment_claims.x', variables: {...} } — either as the message
// itself or serialized as JSON inside a plain message string.
const getStructuredErrorMessage = (message) => {
	if (message && typeof message === 'object') {
		return message.code ? message : null
	}
	if (typeof message !== 'string') return null
	if (babel.te(message)) return { code: message }

	const jsonStart = message.indexOf('{')
	if (jsonStart === -1) return null
	try {
		const payload = JSON.parse(message.slice(jsonStart))
		const items = Array.isArray(payload?.errors) ? payload.errors : [payload]
		for (const item of items) {
			const candidate =
				item?.message && typeof item.message === 'object'
					? item.message
					: item
			if (candidate?.code) return candidate
		}
	} catch (_e) {
		return null
	}
	return null
}

const getTranslatedErrorMessage = (e) => {
	const structured = getStructuredErrorMessage(e.message)
	const code = structured?.code || (babel.te(e.code) ? e.code : null)
	if (!code || !babel.te(code)) return null
	const variables = structured?.variables || e.parameters || e.param || {}
	return babel.t(code, normalizeVariables(variables))
}

const getShipmentErrorMessage = ({ message, parameters }) => {
	if (message === SHIPMENT_VALIDATION_ERROR && parameters?.msg) {
		return babel.t('common.messages.shipment-validation-error', {
			field: parameters.field || parameters.field_path,
			msg: parameters.msg
		})
	}
	if (typeof message === 'string' && message.startsWith(SHIPMENT_MESSAGE_PREFIX)) {
		const code = message.replace(SHIPMENT_MESSAGE_PREFIX, '').replace(/_/g, '-')
		const translationKey = `${SHIPMENT_ERROR_I18N_PREFIX}${code}`
		if (babel.te(translationKey)) {
			return babel.t(translationKey, parameters || {})
		}
	}
	const details = formatParameters(parameters)
	return details ? `${message.replace(EMBEDDED_DETAILS_PATTERN, '')}: ${details}` : message
}

const getDefaultErrorMessage = (e) => {
	if (Array.isArray(e) && e.length) {
		return getDefaultErrorMessage(e[0])
	}
	if (e.errors && e.errors.length) {
		return getDefaultErrorMessage(e.errors[0])

	}
	if (typeof e === 'string') {
		return getTranslatedErrorMessage({ message: e }) || (e.length ? e : null)
	}
	if (e.message) {
		if (e.code === SHIPMENT_ERROR_CODE) return getShipmentErrorMessage(e)
		const translated = getTranslatedErrorMessage(e)
		if (translated) return translated
		const message = e.message
		const param = e.param
		return param ? `${param} ${message}` : message
	}
	return e.length ? e : null
}

const getErrorMessage = (e) => {
	if (e) {
		if (Array.isArray(e) && e.length) {
			return getDefaultErrorMessage(e[0])
		} else {
			if ( e.graphQLErrors?.length) {
				return getDefaultErrorMessage(e.graphQLErrors[0])
			}
			if (e.errors?.length) {
				return getDefaultErrorMessage(e.errors[0])
			}
			if (e.networkError?.result?.errors?.length) {
				return getDefaultErrorMessage(e.networkError?.result?.errors[0])
			}
		}
		return getDefaultErrorMessage(e)
	}
	return null
}

export const getDecoratedErrorMessage = (e) => {
	return getErrorMessage(e) || babel.t('common.messages.error')
}

